import React, { useState } from 'react';
import { provenanceLabel } from '../shared/provenance';
import { ChevronRight } from 'lucide-react';
import { SectionCard } from './SectionCard';
import WorkflowRefLink from '../shared/WorkflowRefLink';
import { ACTION_LABEL, SHARED_CHANGE_LABELS } from './constants';
import { commentLabel, useFieldCommentsContext } from './fieldComments';
import { formatDateTime } from '../../utils/dateFormat';
import { actorLabel } from './eventActor';

// The one request-history renderer (docs/workflow-ui.md §4.4): a vertical
// timeline (who did what, when), and events that carry field changes expand
// into a Field / Before / After table. Collapsed by default; email events are
// never shown (house rule — legacy email_sent rows filtered here
// as a second line of defence).
//
//   events        — [{ id, action, actorEmail, actorName?, reason, createdAt, changes? }]
//                   (the actor prints by name, the email as its tooltip)
//   actionLabels  — per-flow overrides merged over the shared ACTION_LABEL map
//                   (e.g. { applied: ['Applied to QC record', 'bg-green-50 text-green-700'] })
//   changeLabels  — { field: 'Human label' } for the diff table
//   fmtChangeValue— (field, value) => string
//   expandChanges — (changes) => changes; unpack a composite field into real
//                   rows before rendering. QC/QCA pass qcQuestions'
//                   `expandAnswerChanges` so the acceptance form shows one row
//                   per question instead of one unreadable object.
//                   A row may carry its own `label`, which wins
//                   over changeLabels — that is how per-question labels arrive
//                   without a 60-entry map per flow.
//   renderExtra   — (event) => node appended under an event (flow-specific rows)
const defaultFmt = (field, v) => (v === null || v === undefined || v === '' ? '—' : String(v));

// SHARED_CHANGE_LABELS (constants.js) is what makes a removed attachment
// visible: the file itself is deleted on save, and the diff row is the only
// place the swap survives. A page may still override it.

const HistoryEvent = ({ ev, labels, changeLabels, commentLabelFor, fmtChangeValue, expandChanges, renderExtra }) => {
  const [openDiff, setOpenDiff] = useState(false);
  const [label, cls] = labels[ev.action] || [ev.action, 'bg-slate-100 text-slate-600'];
  const changed = Object.entries(expandChanges ? expandChanges(ev.changes || {}) : (ev.changes || {}));

  return (
    <li className="ml-4">
      <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-slate-300" />
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>{label}</span>
        {/* 正式記錄的歷程帶 source / workflowRef（申請單自己的歷程沒有,所以是
            條件式）——「這一列是哪張單促成的」要點得過去。 */}
        {ev.workflowRef && <WorkflowRefLink refNo={ev.workflowRef} />}
        {!ev.workflowRef && provenanceLabel(ev.source) && (
          <span className="text-xs text-slate-500">{provenanceLabel(ev.source)}</span>
        )}
        <span className="text-xs text-slate-500" title={ev.actorName ? ev.actorEmail : undefined}>{actorLabel(ev)}</span>
        <span className="text-xs text-slate-500">{formatDateTime(ev.createdAt)}</span>
      </div>
      {ev.reason && <p className="text-xs text-slate-500 mt-0.5 break-words">{ev.reason}</p>}
      {/* 逐項意見——每一輪退件的都留著。上面的紅框只顯示「現在這一輪」，
          第二次、第三次退件的意見在這裡：歷史的備註保留在
          申請單。內建而不是靠各頁傳 renderExtra：十六條流程都要，漏一頁
          就是同一則意見在某條流程上憑空消失。 */}
      {(ev.fieldComments || []).length > 0 && (
        <ul className="mt-1 ml-0.5 border-l-2 border-red-200 pl-2 space-y-0.5">
          {ev.fieldComments.map((c) => (
            <li key={c.field} className="text-xs text-slate-600 break-words">
              <span className="font-medium text-slate-700">
                {commentLabel(c, commentLabelFor)}:
              </span>{' '}
              <span className="whitespace-pre-wrap">{c.comment}</span>
            </li>
          ))}
        </ul>
      )}
      {changed.length > 0 && (
        <div className="mt-1.5 border border-slate-200 rounded-lg">
          <button
            type="button"
            onClick={() => setOpenDiff((v) => !v)}
            className="w-full flex items-center gap-1.5 px-3 py-1.5 text-left text-xs font-medium text-slate-600 hover:bg-slate-50 rounded-lg"
          >
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${openDiff ? 'rotate-90' : ''}`} />
            {changed.length} field{changed.length === 1 ? '' : 's'} changed
          </button>
          {openDiff && (
            // table-fixed + break-words: an unspaced long value (e.g. a pasted
            // Remarks blob) must wrap inside its 1/3 column, never widen the
            // table past the card.
            <table className="w-full table-fixed text-sm border-t border-slate-200">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="px-3 py-1.5 font-medium w-1/3">Field</th>
                  <th className="px-3 py-1.5 font-medium w-1/3">Before</th>
                  <th className="px-3 py-1.5 font-medium w-1/3">After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {changed.map(([field, diff]) => (
                  <tr key={field}>
                    <td className="px-3 py-1.5 text-slate-700 align-top break-words">{diff.label || changeLabels[field] || field}</td>
                    <td className="px-3 py-1.5 text-slate-500 align-top break-words">{fmtChangeValue(field, diff.before)}</td>
                    <td className="px-3 py-1.5 text-slate-900 align-top break-words">{fmtChangeValue(field, diff.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {renderExtra && renderExtra(ev)}
    </li>
  );
};

const HistoryCard = ({
  events = [],
  actionLabels = {},
  changeLabels = {},
  fmtChangeValue = defaultFmt,
  expandChanges = null,
  renderExtra = null,
  defaultOpen = false,
}) => {
  const visible = events.filter((ev) => ev.action !== 'email_sent');
  const labels = { ...ACTION_LABEL, ...actionLabels };
  const fieldLabels = { ...SHARED_CHANGE_LABELS, ...changeLabels };
  // 退件意見的欄位名走 useFieldComments 的對照表，不是 `changeLabels`：
  // 能留意見的地方比會進差異表的欄位多（QC 的驗收表有六十題，題目名字住在
  // 題庫裡），拿差異表的對照去查，查不到的就會印出 `answers.q_specialists`
  // 這種裸 key。
  const fc = useFieldCommentsContext();
  const commentLabelFor = fc ? fc.labelFor : ((f) => fieldLabels[f] || f);

  return (
    <SectionCard
      title="History"
      count={`${visible.length} event${visible.length === 1 ? '' : 's'}`}
      collapsible
      defaultOpen={defaultOpen}
    >
      {visible.length === 0 ? (
        <p className="text-sm text-slate-500">No history yet.</p>
      ) : (
        <ol className="relative border-l border-slate-200 ml-1.5 space-y-3">
          {visible.map((ev) => (
            <HistoryEvent
              key={ev.id}
              ev={ev}
              labels={labels}
              changeLabels={fieldLabels}
              commentLabelFor={commentLabelFor}
              fmtChangeValue={fmtChangeValue}
              expandChanges={expandChanges}
              renderExtra={renderExtra}
            />
          ))}
        </ol>
      )}
    </SectionCard>
  );
};

export default HistoryCard;
